import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import { launchErrorToast } from '@/utils/toast';

type MessageCallback = (event: MessageEvent) => boolean | void;

const useMessageListener = () => {
  const { captureEvent } = useAnalytics();

  // Keep track of current listener to prevent multiple active listeners
  let currentHandler: ((event: MessageEvent) => void) | null = null;

  const listen = (callback: MessageCallback, allowedOrigin?: string) => {
    // Clean up any existing listener first
    if (currentHandler) {
      window.removeEventListener('message', currentHandler);
      currentHandler = null;
    }

    const handler = (event: MessageEvent) => {
      if (allowedOrigin && event.origin !== allowedOrigin) {
        return;
      }

      const handled = callback(event);

      if (event.data.error) {
        captureEvent(EVENTS.POPUP_WINDOW_ERROR, { error: event.data.error });
        launchErrorToast();
      }

      // Send ACK back to popup
      if (event.source && event.source !== window) {
        (event.source as Window).postMessage({ type: 'ACK' }, event.origin);
      }

      if (handled) {
        window.removeEventListener('message', handler);
        currentHandler = null;
      }
    };

    currentHandler = handler;
    window.addEventListener('message', handler);

    // Return cleanup function
    return () => {
      window.removeEventListener('message', handler);
      currentHandler = null;
    };
  };

  return listen;
};
export default useMessageListener;
